import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { anunciosService, fretesService, getFotoUrl } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import './MeusAnuncios.css'

function MeusAnuncios() {
    const [anuncios, setAnuncios] = useState([])
    const [loading, setLoading] = useState(true)
    const [candidaturas, setCandidaturas] = useState({})
    const [candidaturaExpandida, setCandidaturaExpandida] = useState(null)
    const [anuncioParaVender, setAnuncioParaVender] = useState(null)
    const [anuncioBloqueado, setAnuncioBloqueado] = useState(null)
    const { usuario, isLogado } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        if (!isLogado) {
            navigate('/login')
            return
        }
        if (isLogado) {
            carregarAnuncios()
        }
    }, [isLogado, usuario, navigate])

    const carregarAnuncios = async () => {
        setLoading(true)
        try {
            // Em uma implementação real, teríamos um endpoint específico para "meus anúncios"
            // Aqui vamos filtrar da lista geral por enquanto, ou usar um endpoint mockado se existisse
            const response = await anunciosService.listar({ vendedorId: usuario.id })

            // A API já filtra pelo vendedorId
            const meusAnuncios = response.anuncios || []
            setAnuncios(meusAnuncios)

            // Carregar contagem de candidaturas para cada anúncio
            // Isso seria melhor feito no backend, mas vamos fazer aqui por enquanto
            meusAnuncios.forEach(anuncio => {
                carregarCandidaturas(anuncio.id)
            })

        } catch (error) {
            console.error('Erro ao buscar anúncios:', error)
        } finally {
            setLoading(false)
        }
    }

    const carregarCandidaturas = async (anuncioId) => {
        try {
            const response = await fretesService.candidaturasPorAnuncio(anuncioId)
            setCandidaturas(prev => ({
                ...prev,
                [anuncioId]: response.candidaturas
            }))
        } catch (error) {
            console.error(`Erro ao buscar candidaturas do anúncio ${anuncioId}:`, error)
        }
    }

    const handleAceitarCandidatura = async (candidaturaId, anuncioId) => {
        try {
            await fretesService.aceitar(candidaturaId, 'Sua proposta foi aceita! Entre em contato para combinarmos.')
            alert('Candidatura aceita com sucesso!')
            carregarCandidaturas(anuncioId)
        } catch (error) {
            alert('Erro ao aceitar candidatura: ' + error.message)
        }
    }

    const handleRejeitarCandidatura = async (candidaturaId, anuncioId) => {
        if (!confirm('Tem certeza que deseja rejeitar esta proposta?')) return

        try {
            await fretesService.rejeitar(candidaturaId, 'Obrigado pelo interesse, mas fechei com outro motorista.')
            alert('Candidatura rejeitada.')
            carregarCandidaturas(anuncioId)
        } catch (error) {
            alert('Erro ao rejeitar candidatura: ' + error.message)
        }
    }

    const handleDeletarAnuncio = async (id) => {
        if (!confirm('Tem certeza que deseja excluir este anúncio?')) return

        try {
            await anunciosService.deletar(id)
            setAnuncios(anuncios.filter(a => a.id !== id))
            alert('Anúncio excluído com sucesso!')
        } catch (error) {
            console.error('Erro ao excluir anúncio:', error)
            alert('Erro ao excluir anúncio: ' + error.message)
        }
    }

    const handleEditarClick = (anuncio) => {
        // Usa criadoEm se existir, ou criado_em como fallback
        const dataCriacao = new Date(anuncio.criadoEm || anuncio.criado_em);
        const agora = new Date();
        const diffHoras = (agora - dataCriacao) / (1000 * 60 * 60);

        if (diffHoras > 24) {
            setAnuncioBloqueado(anuncio);
        } else {
            navigate(`/editar-anuncio/${anuncio.id}`);
        }
    }

    const formatPrice = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0
        }).format(value)
    }

    if (loading) return <div className="loading">Carregando seus anúncios...</div>

    return (
        <div className="meus-anuncios-page">
            <div className="page-header">
                <h1>Meus Anuncios</h1>
                <div className="header-actions">
                    <button className="btn btn-primary" onClick={() => navigate('/criar-anuncio')}>
                        + Novo Anúncio
                    </button>
                </div>
            </div>

            <div className="anuncios-list">
                {anuncios.length > 0 ? (
                    anuncios.map(anuncio => {
                        const listaCandidaturas = candidaturas[anuncio.id] || []
                        const pendentes = listaCandidaturas.filter(c => c.status === 'pendente').length

                        const rawMediaUrl = anuncio.imagens?.[0] || anuncio.imagem
                        const isVideo = rawMediaUrl && (
                            rawMediaUrl.match(/\.(mp4|mov|avi|webm)$/i) || 
                            rawMediaUrl.includes('/video/upload/') ||
                            (anuncio.midias?.length > 0 && anuncio.midias[0].tipo === 'video')
                        )
                        const mediaUrl = rawMediaUrl ? getFotoUrl(rawMediaUrl) : 'https://via.placeholder.com/400x300?text=Sem+Foto'

                        return (
                            <div key={anuncio.id} className="anuncio-item-card">
                                <div className="anuncio-main-info">
                                    <div className="anuncio-thumb-wrapper">
                                        <img src={mediaUrl} alt={anuncio.titulo} className="anuncio-thumb" />
                                        {isVideo && (
                                            <div className="video-play-overlay">
                                                <span className="play-icon">▶</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="anuncio-dados">
                                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {anuncio.titulo}
                                            {anuncio.isExpirado && <span style={{ backgroundColor: '#ef4444', color: 'white', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Expirado</span>}
                                        </h3>
                                        <p className="anuncio-meta">
                                            {formatPrice(anuncio.preco)} • {anuncio.quantidade} cabeças • {anuncio.raca}
                                        </p>
                                        <div className="anuncio-stats">
                                            <span>👁️ {anuncio.visualizacoes || 0} visualizações</span>
                                            <span>📅 {new Date(anuncio.criadoEm || anuncio.criado_em).toLocaleDateString()}</span>
                                        </div>
                                    </div>

                                    <div className="anuncio-acoes">
                                        <button
                                            className="btn btn-success btn-sm"
                                            onClick={() => setAnuncioParaVender(anuncio)}
                                            style={{ fontWeight: '600' }}
                                        >
                                            🤝 Vendido
                                        </button>
                                        <button 
                                            className="btn btn-outline btn-sm"
                                            onClick={() => handleEditarClick(anuncio)}
                                        >
                                            ✏️ Editar
                                        </button>
                                        <button
                                            className="btn btn-outline btn-danger btn-sm"
                                            onClick={() => handleDeletarAnuncio(anuncio.id)}
                                        >
                                            🗑️ Excluir
                                        </button>
                                        <Link to={`/anuncio/${anuncio.id}`} className="btn btn-outline btn-sm">
                                            👁️ Ver
                                        </Link>

                                        <button
                                            className={`btn btn-sm ${pendentes > 0 ? 'btn-warning' : 'btn-secondary'}`}
                                            onClick={() => setCandidaturaExpandida(candidaturaExpandida === anuncio.id ? null : anuncio.id)}
                                        >
                                            🚚 Fretes ({listaCandidaturas.length})
                                            {pendentes > 0 && <span className="badge-pendente">{pendentes} novos</span>}
                                        </button>
                                    </div>
                                </div>

                                {/* Área de Candidaturas Expandida */}
                                {candidaturaExpandida === anuncio.id && (
                                    <div className="candidaturas-area">
                                        <h4>Candidaturas de Frete</h4>

                                        {listaCandidaturas.length === 0 ? (
                                            <p className="sem-candidaturas-msg">Nenhuma candidatura recebida ainda.</p>
                                        ) : (
                                            <div className="candidaturas-grid">
                                                {listaCandidaturas.map(cand => (
                                                    <div key={cand.id} className={`candidatura-item ${cand.status}`}>
                                                        <div className="motorista-info">
                                                            <strong>{cand.motorista_nome}</strong>
                                                            <div className="motorista-rating">⭐ 4.9 (Exemplo)</div>
                                                        </div>

                                                        <div className="proposta-info">
                                                            <div className="proposta-valor">
                                                                R$ {formatPrice(cand.preco_proposto)}
                                                            </div>
                                                            <p className="proposta-msg">"{cand.mensagem}"</p>
                                                        </div>

                                                        <div className="proposta-status">
                                                            Status: <strong>{cand.status.toUpperCase()}</strong>
                                                        </div>

                                                        {cand.status === 'pendente' && (
                                                            <div className="proposta-acoes">
                                                                <button
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={() => handleAceitarCandidatura(cand.id, anuncio.id)}
                                                                >
                                                                    ✅ Aceitar
                                                                </button>
                                                                <button
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleRejeitarCandidatura(cand.id, anuncio.id)}
                                                                >
                                                                    ❌ Rejeitar
                                                                </button>
                                                            </div>
                                                        )}

                                                        {cand.status === 'aceita' && (
                                                            <div className="contato-motorista">
                                                                <button className="btn btn-primary btn-sm btn-block">
                                                                    📞 Ver Contato
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })
                ) : (
                    <div className="sem-anuncios">
                        <h2>Você ainda não tem anúncios</h2>
                        <p>Comece a vender agora mesmo!</p>
                        <button className="btn btn-primary" onClick={() => navigate('/criar-anuncio')}>
                            Criar Primeiro Anúncio
                        </button>
                    </div>
                )}
            </div>

            {anuncioParaVender && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    backdropFilter: 'blur(10px)'
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        padding: '2.5rem',
                        maxWidth: '500px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
                    }}>
                        <div style={{ fontSize: '3.5rem', marginBottom: '1.5rem' }}>🤝🎉</div>
                        <h2 style={{ color: '#f8fafc', marginBottom: '1rem', fontSize: '1.75rem', fontWeight: 'bold' }}>Parabéns pela Venda!</h2>
                        <p style={{ color: '#94a3b8', marginBottom: '2rem', lineHeight: '1.6' }}>
                            Você está prestes a marcar o anúncio <strong>"{anuncioParaVender.titulo}"</strong> como vendido. <br /><br />
                            Ele será retirado das buscas públicas da plataforma e adicionado ao seu histórico de vendas realizadas.
                        </p>
                        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                            <button 
                                className="btn btn-outline"
                                style={{ padding: '12px 25px', borderRadius: '8px', fontWeight: 'bold' }}
                                onClick={() => setAnuncioParaVender(null)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn btn-success"
                                style={{ 
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)', 
                                    border: 'none', 
                                    color: '#fff', 
                                    fontWeight: 'bold',
                                    padding: '12px 25px',
                                    borderRadius: '8px'
                                }}
                                onClick={async () => {
                                    try {
                                        await anunciosService.marcarVendido(anuncioParaVender.id);
                                        setAnuncios(anuncios.filter(a => a.id !== anuncioParaVender.id));
                                        setAnuncioParaVender(null);
                                        alert('Venda computada com sucesso!');
                                    } catch (error) {
                                        alert('Erro ao marcar como vendido: ' + error.message);
                                    }
                                }}
                            >
                                Confirmar Venda
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Anúncio Bloqueado para Edição (> 24h) */}
            {anuncioBloqueado && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.9)',
                    zIndex: 9999999,
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto'
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        padding: '2.5rem',
                        maxWidth: '500px',
                        width: '100%',
                        margin: 'auto',
                        textAlign: 'center',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
                    }}>
                        <div style={{ fontSize: '3.5rem', marginBottom: '1.5rem' }}>🔒</div>
                        <h2 style={{ color: '#f8fafc', marginBottom: '1rem', fontSize: '1.5rem', fontWeight: 'bold' }}>Edição Bloqueada</h2>
                        <p style={{ color: '#94a3b8', marginBottom: '2rem', lineHeight: '1.6' }}>
                            O prazo de 24 horas para edição expirou. Por medidas de segurança da plataforma, não é mais possível editar os dados estruturais deste anúncio.<br /><br />
                            Caso precise alterar algo crítico, entre em contato com nosso suporte via e-mail ou WhatsApp informando o motivo.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '1.5rem' }}>
                            <a 
                                href="https://wa.me/5531996205443" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn btn-success"
                                style={{ background: '#25D366', color: 'white', fontWeight: 'bold', padding: '12px', borderRadius: '8px', textDecoration: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                            >
                                📱 Falar no WhatsApp
                            </a>
                            <a 
                                href="mailto:contato@agroov.com" 
                                className="btn btn-primary"
                                style={{ fontWeight: 'bold', padding: '12px', borderRadius: '8px', textDecoration: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                            >
                                ✉️ Enviar E-mail
                            </a>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                className="btn btn-outline"
                                style={{ padding: '10px 25px', borderRadius: '8px', fontWeight: 'bold', width: '100%' }}
                                onClick={() => setAnuncioBloqueado(null)}
                            >
                                Voltar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MeusAnuncios
